import { useState, type ReactNode } from "react";
import { FoldToggle } from "../ui";
import { clsx } from "clsx";
import { useTranslation } from "react-i18next";
import { HelpLink } from "./HelpLink";
import { settingsHelp } from "./help";

const KEY = "drang.settings.folds";

function readFolds(): Record<string, boolean> {
  try {
    return JSON.parse(localStorage.getItem(KEY) ?? "{}") as Record<string, boolean>;
  } catch {
    return {};
  }
}

/**
 * A group of Settings sections under one heading (owner, 2026-10-24: interface, data coming in, data going out,
 * people and devices). A group whose sections are all hidden at the current level shows nothing.
 */
export function SettingsGroup({
  title,
  hint,
  testId,
  children,
}: {
  title: string;
  /** who the group's settings affect, in one sentence */
  hint?: string;
  testId?: string;
  children: ReactNode;
}) {
  return (
    <div className="mt-8 first:mt-6 [&:not(:has(section))]:hidden" data-testid={testId}>
      <h2 className="text-[15px] font-semibold">{title}</h2>
      {hint && <p className="text-[12.5px] text-neutral-500">{hint}</p>}
      {children}
    </div>
  );
}

/**
 * One block of Settings: a header with a fold triangle, a short explanation that is always shown, and the
 * content while open. Open or folded is the person's choice, remembered on this device; large blocks start
 * folded. `intro` is the explanation as written; when it is longer than one sentence, `summary` is the
 * one-sentence version shown while the block is folded.
 */
export function SettingsSection({
  id,
  title,
  testId,
  folded = false,
  intro,
  summary,
  details,
  control,
  children,
}: {
  id: string;
  title: string;
  testId?: string;
  /** start folded until the person opens it */
  folded?: boolean;
  intro?: string;
  summary?: string;
  /** more explanation under the intro while the block is open, folded behind "Details" */
  details?: ReactNode;
  /** a control shown beside the title instead of a fold: the section is always open (the language choice) */
  control?: ReactNode;
  children?: ReactNode;
}) {
  const { t } = useTranslation();
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [open, setOpen] = useState(() => readFolds()[id] ?? !folded);
  if (control) {
    return (
      <section className="kss-surface mt-4 p-4" data-testid={testId} data-open>
        <div className="flex items-center gap-3">
          <h2 className="text-xs font-semibold uppercase tracking-[0.06em] text-neutral-500">{title}</h2>
          {control}
          <span className="flex-1" />
          <HelpLink page={settingsHelp(id)} className="h-6 w-6 touch:h-11 touch:w-11" />
        </div>
        {intro && (
          <p className="mt-2 text-[12.5px] text-neutral-500" data-testid="section-intro">
            {intro}
          </p>
        )}
        {children}
      </section>
    );
  }
  const toggle = () => {
    const next = !open;
    setOpen(next);
    try {
      localStorage.setItem(KEY, JSON.stringify({ ...readFolds(), [id]: next }));
    } catch {
      /* a convenience only */
    }
  };
  return (
    <section className="kss-surface mt-4 p-4" data-testid={testId} data-open={open}>
      <div className="flex items-center gap-1">
        <h2 className="min-w-0 flex-1">
          <FoldToggle open={open} onToggle={toggle} heading testId="section-toggle">
            {title}
          </FoldToggle>
        </h2>
        <HelpLink page={settingsHelp(id)} className="h-6 w-6 touch:h-11 touch:w-11" />
      </div>
      {intro && (
        <p className="mt-2 text-[12.5px] text-neutral-500" data-testid="section-intro">
          {open ? intro : (summary ?? intro)}
        </p>
      )}
      {open && details && (
        <div className="mt-1.5 flex flex-col gap-1.5">
          <FoldToggle
            open={detailsOpen}
            onToggle={() => setDetailsOpen((v) => !v)}
            testId="section-details-toggle"
          >
            {t("settings.details")}
          </FoldToggle>
          {detailsOpen && (
            <div
              className="flex flex-col gap-1.5 pl-4 text-[12.5px] text-neutral-500"
              data-testid="section-details"
            >
              {details}
            </div>
          )}
        </div>
      )}
      {open && <div className="mt-3 flex flex-col gap-3">{children}</div>}
    </section>
  );
}

export type StatusTone = "ok" | "warn" | "err" | "accent" | "neutral" | "quiet";

/** A status in words, colour-coded but without a box (calmer than a pill), 11 px: one step below labels. */
export function StatusText({
  tone = "neutral",
  children,
  title,
  testId,
  source,
}: {
  tone?: StatusTone;
  children: ReactNode;
  title?: string;
  testId?: string;
  source?: string;
}) {
  return (
    <span
      title={title}
      data-testid={testId}
      data-source={source}
      className={clsx(
        "text-[11px]",
        tone === "ok" && "text-ok",
        tone === "warn" && "text-warn",
        tone === "err" && "text-err",
        tone === "accent" && "text-accent",
        tone === "neutral" && "text-neutral-500",
        tone === "quiet" && "text-neutral-400",
      )}
    >
      {children}
    </span>
  );
}
